import { formatDateTimeForGraphLabel } from "@src/cloud-connect/utils/format-date-time-for-graph-label";
import store from "@src/reducers/store";
import { formatChartTooltipDate } from "@src/cloud-connect/utils/timezone-display-utils";
import { getMachineStructureNodeset } from "@src/cloud-connect/dependency-injection-container/get-machine-structure-nodeset";
import { preconfiguredColors } from "./graph-color-palette";
import { CreateAncestorsLineageForSidePanel } from "@src/cloud-connect/node-manager/entities/create-ancestors-lineage-for-side-panel";
import { searchUnitsList } from "../unit-input-and-popover/search-units-list";
import { formatPathFromSegments } from "@src/cloud-connect/utils/format-path-from-segments";
import { ToolboxBuilder } from "@src/cloud-connect/charts/toolbox-builder";
import { GridBuilder } from "@src/cloud-connect/charts/grid-builder";
import { XAxisBuilder } from "@src/cloud-connect/charts/xaxis-builder";
// Import the OurNodeset class

// Function to build the ECharts option object dynamically
export const buildRawDataGraphOptionObject = (
  tileTitle,
  arrayOfNodes,
  nodes,
) => {
  if (!arrayOfNodes) return null;

  const arrayOfComputedUnits = [];

  // Step 1: Filter arrayOfNodes to include only those with status 200
  const validNodes = arrayOfNodes.filter((node) => {
    if (node.status === 200 && node.body.meta_data) {
      if (
        node.body.meta_data.timeseries_type === "computed" &&
        node.body.meta_data.unit
      ) {
        arrayOfComputedUnits.push(
          searchUnitsList(node.body.meta_data.unit.toString())[0].display_name,
        );
      }
      return node;
    }
  });

  if (!validNodes.length) return null;

  // Step 2: Extract the times array from the first valid node's aggregated values
  let timesArray = [];
  for (let i = 0; i < validNodes.length; i++) {
    if (validNodes[i]?.body?.values) {
      timesArray = validNodes[i].body.values.map((value) => value.time);
      break; // Exit the loop once a valid time array is found
    }
  }

  // Step 3: Build the series array dynamically based on the provided nodes
  const seriesArray = nodes.map((node, index) => {
    // Find the corresponding valid node by matching the source with the node id
    const correspondingNode = validNodes.find(
      (item) => item.body.meta_data.source === node.id,
    );

    // Get the number of timestamps or data points from the first valid series
    const dataLength = timesArray.length;

    // Extract the average values (v_avg) for the series data

    const buildAncestorBreadCrumbs = () => {
      return formatPathFromSegments(
        new CreateAncestorsLineageForSidePanel().createMachineStructureAncestorsLineageForSidePanel(
          node.id,
        ),
        { trimStart: true, trimEnd: true },
      );
    };

    const data = correspondingNode
      ? correspondingNode.body.values.map((value) => ({
          value: value.value,
          location: buildAncestorBreadCrumbs(),
        }))
      : Array(dataLength).fill({
          value: null,
          location: buildAncestorBreadCrumbs(),
        });

    const uniqueIdentifier = `${node.name} (${index + 1})`;
    // Return the series object for this node

    return {
      name: uniqueIdentifier,
      data: data,
      type: node.chart_type || "line", // Use node's chart_type or default to 'line'
      smooth: 0.4,
      itemStyle: {
        color: preconfiguredColors[index % preconfiguredColors.length], // Use color for symbols
      },
      lineStyle: {
        width: 2,
        color: preconfiguredColors[index % preconfiguredColors.length],
      },
      symbol: "circle",
      symbolSize: 8,
      showSymbol: data.length === 1 ? true : false,
      animation: true,
      animationDuration: 1000,
      animationEasing: "quadraticIn",
      animationDelay: 0,
      connectNulls: true,
    };
  });

  // Step 4: Use an array to collect all the units

  let units;

  if (arrayOfComputedUnits.length > 0) {
    units = arrayOfComputedUnits;
  } else {
    units = nodes.map((node) => {
      return getMachineStructureNodeset().getNodeById(node.id)?.attributes?.unit
        ?.display_name; // Retrieve display_name or null
    });
  }

  // Step 5: Determine Y-axis name
  const firstUnit = units[0]; // Take the first unit as reference
  const allSameUnit = units.every(
    (unit) => unit === firstUnit && unit !== null,
  ); // Check if all are the same and not null

  const yAxisName = allSameUnit && firstUnit ? firstUnit : "Unit"; // Use the display name if all are the same, otherwise "Unit"

  const setYAxisType = () => {
    return typeof arrayOfNodes[0].body.values[0].value === "number"
      ? "value"
      : "category";
  };

  // Step 6: Build and return the final graph options object
  const graphOptions = {
    color: preconfiguredColors,
    grid: new GridBuilder().build(),
    toolbox: new ToolboxBuilder()
      .withDataView()
      .withSaveAsImage({ name: tileTitle })
      .build(),
    xAxis: new XAxisBuilder()
      .setData(timesArray)
      .setFormatter(formatDateTimeForGraphLabel)
      .build(),
    yAxis: {
      name: yAxisName, // Use the determined name here
      type: setYAxisType(),
      scale: true,
      axisLine: {
        onZero: true,
      },
      nameTextStyle: {
        padding: [-11, 0, 0, -20],
        verticalAlign: "top",
        align: "left",
      },
    },
    tooltip: {
      trigger: "axis",
      backgroundColor: "rgba(255, 255, 255, 0.3)", // Semi-transparent background
      confine: true,
      borderColor: "gray",
      borderWidth: 1,
      textStyle: {
        fontFamily: "Inter", // Custom font family (or replace with preferred font)
      },
      formatter: function (params) {
        const timezone = store.getState().yourSlice.timezone;
        const language = store.getState().yourSlice.language;
        const formattedDate = formatChartTooltipDate(
          params[0].axisValue,
          timezone,
          language,
        );
        // Creating HTML for the tooltip with customized styles
        let tooltipHTML = `<div style="font-size: 10px; color: white; font-weight: 100; line-height: 12px; letter-spacing: -2%; padding-bottom: 5px;">
                                    ${formattedDate}
                              </div>`;

        params.forEach((item, index) => {
          const location = item.data.location;
          const value = item.data.value;
          const cleanName = item.seriesName.replace(/\s\(\d+\)/, "");
          tooltipHTML += `
                    <div style="display: flex; flex-direction: column;">
                      <div style="display: flex; gap: 0 5px; justify-content: space-between; align-items: center">
                        <div style="display: flex; gap: 0 5px; align-items: center">
                          <span style="color: ${
                            preconfiguredColors[
                              index % preconfiguredColors.length
                            ]
                          };">${item.marker}</span> 
                          <span style="color: white; font-weight: 100;">${cleanName}</span>
                        </div>
                        <span style="color: white; font-weight: 800;">${value}</span>
                      </div>
                      <span style="padding-left: 20px; color: white; font-size: 10px; margin-top: -3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex-grow: 1; flex-shrink: 1;">${location}</span>
                    </div>`;
        });

        return tooltipHTML;
      },
      extraCssText:
        "backdrop-filter: blur(5px); padding: 15px 20px 10px; box-sizing: border-box;",
    }, // Add box-sizing to include padding    },
    series: seriesArray, // Dynamic series built from valid nodes and provided nodes
    legend: {
      show: true,
      orient: "horizontal",
      left: "center",
      bottom: 0,
      align: "auto",
      padding: 5,
      itemGap: 10,
      textStyle: {
        color: "#e6f1f2",
      },
      formatter: function (name) {
        // Remove the index/number from the legend display (if any)
        return name.replace(/\s\(\d+\)/, ""); // This removes the index/number from the legend
      },
    },
  };

  return graphOptions;
};
